import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { mangas, categories } from '@/mocks/manga';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];
  const [activeTab, setActiveTab] = useState(slug || 'all');

  const filteredMangas = useMemo(() => {
    if (activeTab === 'all') return mangas;
    if (activeTab === 'latest') {
      return [...mangas].sort(
        (a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime()
      );
    }
    return mangas.filter((m) => m.categorySlug === activeTab);
  }, [activeTab]);

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 刃牙漫画在线观看`,
    description: currentCategory.description,
    url: `https://baki-manga.com/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '刃牙漫画在线观看',
      url: 'https://baki-manga.com',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://baki-manga.com' },
      { '@type': 'ListItem', position: 2, name: `${currentCategory.name}`, item: `https://baki-manga.com/category/${slug}` },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${currentCategory.name} - 刃牙漫画`}
        description={currentCategory.description}
        canonicalUrl={`https://baki-manga.com/category/${slug}`}
        schema={{ ...categorySchema, ...breadcrumbSchema }}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: currentCategory.name },
              ]}
            />

            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-2">
                {currentCategory.name}
              </h1>
              <p className="text-sm text-foreground-600">
                {currentCategory.description}，共 {filteredMangas.length} 部作品
              </p>
            </div>

            <div className="flex items-center gap-1.5 mb-8 overflow-x-auto pb-2">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => {
                    setActiveTab(cat.slug);
                    window.history.replaceState(null, '', `/category/${cat.slug}`);
                  }}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                    activeTab === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-5 md:gap-6 pb-16">
              {filteredMangas.map((manga) => (
                <Link
                  key={manga.slug}
                  to={`/article/${manga.slug}`}
                  className="group"
                >
                  <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-3 bg-background-200">
                    <img
                      src={manga.coverImage}
                      alt={manga.title}
                      title={`${manga.title} - 刃牙漫画在线观看`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-2 left-2">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${
                        manga.status === '连载中'
                          ? 'bg-accent-500 text-background-50'
                          : 'bg-background-800/80 text-background-100'
                      }`}>
                        {manga.status}
                      </span>
                    </div>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors truncate">
                    {manga.title}
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex items-center gap-0.5">
                      <i className="ri-star-fill text-accent-500 text-xs"></i>
                      <span className="text-xs text-foreground-600">{manga.rating}</span>
                    </div>
                    <span className="text-xs text-foreground-400">{manga.author}</span>
                  </div>
                </Link>
              ))}
            </div>

            {filteredMangas.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-book-2-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">暂无该分类的漫画作品</p>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}